import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  CheckIcon,
  EyeIcon,
  FileIcon,
  PencilIcon,
  TriangleAlertIcon,
  ViewIcon,
} from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { styled } from "simplydo/react/styled";
import { mobileOnly, useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Placeholder, PlaceholderLine } from "simplydo/react/ui/placeholder";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { IdeaFollowButton } from "components/lib/FollowButton";
import { IdeaChallengeHelp, SubmitIdeaButton } from "components/lib/Ideas";
import { Sticky } from "components/lib/UI";

import { useIdea } from "./hooks/useIdea";
import type { IdeaCollaboratorUser } from "./types";

const MobileContainer = styled.div`
  width: 100%;
  padding: 0 5px;
`;

const IdeaStatusBarDiv = styled.div`
  height: ${({ theme }) => (theme.sizes.isMobile ? 40 : 50)}px;
  padding: ${({ theme }) => (theme.sizes.isMobile ? "5px 5px" : "7.5px 15px")};
  background-image: ${({ theme }) =>
    `linear-gradient(to bottom right, ${theme.secondaryColour}, ${util.adjustColour(theme.secondaryColour, 0.5)})`};
  display: flex;
  align-items: center;
  top: 50px;
  width: 100%;
`;

const StatusLabel = styled(Label)`
  min-width: max-content;
  height: 27px;
  display: flex !important;
  align-items: center;
`;

const StatusbarInnerContainer = styled.div<{ $flexGrow: number }>`
  display: flex;
  align-items: center;
  flex-direction: row;
  align-items: center;
  flex-grow: ${({ $flexGrow }) => $flexGrow};
  min-width: ${({ $flexGrow }) => ($flexGrow === 0 ? "max-content" : 0)};
`;

const StatusbarContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

type IdeaStatusBarProps = {
  updateIdea: (data: unknown, path: string[], replace?: boolean) => void;
  isSaving: boolean;
  unsaved: boolean;
  isEditing: boolean;
  setEditing: (isEditing: boolean) => void;
  savedAt?: string | Date | "";
  onSubmit: (isSubmitted: boolean) => void;
  scrollToRequired?: (sectionId: string) => void;
  otherUsers: Record<string, IdeaCollaboratorUser>;
  websocketConnected: boolean;
};

const IdeaStatusBar = ({
  updateIdea,
  isSaving,
  unsaved,
  isEditing,
  setEditing,
  onSubmit,
  otherUsers = {},
  websocketConnected,
}: IdeaStatusBarProps) => {
  const [idea, ideaQuery] = useIdea();
  const loading = ideaQuery.isLoading;

  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const canShowHelpModal = useMemo(
    () => idea?.ownerChallenge?.stage === "published" && isEditing,
    [idea, isEditing],
  );

  const otherUsersValues = useMemo(
    () => Object.values(otherUsers) as IdeaCollaboratorUser[],
    [otherUsers],
  );
  const theme = useTheme();

  const StatusbarOuterContainer = useMemo(
    () => (theme.sizes.isMobile ? MobileContainer : Container),
    [theme],
  );

  return (
    <Sticky top={50} style={{ zIndex: 300 }}>
      <IdeaStatusBarDiv theme={theme}>
        {idea && canShowHelpModal ? (
          <Modal open={helpModalOpen} onClose={() => setHelpModalOpen(false)}>
            <ModalHeader>
              {t("common:capitalise", { key: "generic.challenge" })} overview
            </ModalHeader>
            <ModalContent scrolling>
              <IdeaChallengeHelp challengeId={idea?.challenge} />
            </ModalContent>
            <ModalActions>
              <Button onClick={() => setHelpModalOpen(false)}>
                {t("generic.close")}
              </Button>
            </ModalActions>
          </Modal>
        ) : null}

        <StatusbarOuterContainer>
          <StatusbarContainer>
            <StatusbarInnerContainer $flexGrow={1}>
              {loading ? (
                <Placeholder style={{ width: 150 }}>
                  <PlaceholderLine style={{ height: 25 }} />
                </Placeholder>
              ) : !theme.sizes.isMobile ? (
                idea?.isSubmitted ? (
                  <Popup
                    trigger={
                      <StatusLabel
                        size="small"
                        color="green"
                        style={{ marginRight: "0.5rem" }}
                      >
                        {t("generic.submitted")}
                      </StatusLabel>
                    }
                  >{`Submitted ${formatDate(idea?.submittedAt)}`}</Popup>
                ) : (
                  <StatusLabel
                    size="small"
                    color="yellow"
                    style={{ marginRight: "0.5em" }}
                  >
                    <Icon icon={FileIcon} /> {t("generic.draft")}
                  </StatusLabel>
                )
              ) : null}
              {canShowHelpModal && !theme.sizes.isMobile ? (
                <Popup
                  trigger={
                    <Button
                      size="small"
                      onClick={() => setHelpModalOpen(true)}
                      variant="outline"
                      color="onSecondary"
                    >
                      <Icon icon={EyeIcon} />
                    </Button>
                  }
                >
                  {t("challenges.viewDetails")}
                </Popup>
              ) : null}
              {idea?.ownerChallenge ? (
                <ButtonLink
                  style={{
                    overflow: "hidden",
                    maxWidth: 250,
                    minWidth: 0,
                    justifyContent: "flex-start",
                  }}
                  title={idea.ownerChallenge.name}
                  size="small"
                  variant="outline"
                  to={`/challenges/${idea?.challenge}?noRedirect`}
                  color="onSecondary"
                >
                  <span
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    {idea.ownerChallenge.name}
                  </span>
                </ButtonLink>
              ) : null}
              {!theme.sizes.isMobile && <IdeaFollowButton size="small" />}
            </StatusbarInnerContainer>

            <StatusbarInnerContainer $flexGrow={0}>
              {isEditing && !websocketConnected && !theme.sizes.isMobile ? (
                <Popup
                  trigger={
                    <Icon
                      color="yellow"
                      size="large"
                      icon={TriangleAlertIcon}
                    />
                  }
                >
                  {
                    <>
                      <H4>
                        Connection issues
                        <Subheader>
                          {`Collaboration features are disabled. Your ${t("generic.ideas")} will still be saved automatically.`}
                        </Subheader>
                      </H4>
                    </>
                  }
                </Popup>
              ) : null}

              {!theme.sizes.isMobile ? (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {otherUsersValues.length ? (
                    <Popup
                      trigger={
                        <div>
                          {otherUsersValues.map((u, index) => (
                            <ImageWithFallback
                              key={u._id}
                              avatar
                              src={util.avatarUrl(u)}
                              fallbackSrc={util.avatarUrl()}
                              style={{
                                marginLeft: index === 0 ? 0 : -8,
                                minWidth: 25,
                              }}
                            />
                          ))}
                        </div>
                      }
                    >
                      {
                        <>
                          <H4>
                            Collaborators online
                            <Subheader>
                              {otherUsersValues
                                .map((u) => u.profile?.fullName)
                                .join(", ")}
                            </Subheader>
                          </H4>
                        </>
                      }
                    </Popup>
                  ) : null}
                </div>
              ) : null}

              {loading ? (
                <Placeholder style={{ width: 150 }}>
                  <PlaceholderLine style={{ height: 25 }} />
                </Placeholder>
              ) : (util.canEditIdea(user, idea) ||
                  util.canManageChallenge(user, idea?.ownerChallenge) ||
                  util.hasPermission(
                    user,
                    "challenge.updateIdeaStatus",
                    idea?.challenge,
                  )) &&
                !idea.children ? (
                <>
                  {idea?.isSubmitted &&
                  (util.hasPermission(
                    user,
                    "challenge.editIdeas",
                    idea?.challenge,
                  ) ||
                    util.hasPermission(
                      user,
                      "org.manageChallenges",
                      idea?.ownerChallenge?.organisation,
                    )) ? (
                    <Button
                      size="small"
                      onClick={() => {
                        setEditing(!isEditing);
                      }}
                      color="secondary"
                    >
                      <Icon icon={isEditing ? CheckIcon : PencilIcon} />
                      {isEditing ? "Done editing" : `Edit ${t("generic.idea")}`}
                    </Button>
                  ) : null}

                  {!idea?.isSubmitted && util.canEditIdea(user, idea) ? (
                    <Button
                      size="small"
                      onClick={() => {
                        setEditing(!isEditing);
                        if (isEditing) {
                          api.journey.record(
                            "ideas",
                            idea?._id,
                            "previewedIdea",
                          );
                        } else {
                          api.journey.record(
                            "ideas",
                            idea?._id,
                            "resumedEditingIdea",
                          );
                        }
                      }}
                      color="secondary"
                    >
                      <Icon icon={ViewIcon} />
                      {t(
                        isEditing
                          ? "ideas.header.preview"
                          : "ideas.header.edit",
                      )}
                    </Button>
                  ) : null}

                  <SubmitIdeaButton
                    updateIdea={updateIdea}
                    onSubmit={onSubmit}
                    isSaving={isSaving}
                    unsaved={unsaved}
                    isStatusBar={true}
                    size="small"
                  />
                </>
              ) : null}
            </StatusbarInnerContainer>
          </StatusbarContainer>
        </StatusbarOuterContainer>
      </IdeaStatusBarDiv>
    </Sticky>
  );
};

export default IdeaStatusBar;
